/**
 * ScanPage - Main orchestration page for scan -> extract -> grade -> display flow
 *
 * Flow:
 * 1. ScanUpload: Capture/upload worksheet images
 * 2. QR Detection: Identify worksheet from QR code
 * 3. Upload: Send to backend
 * 4. Extract: Trigger AI extraction and grading
 * 5. Display: Show GradingResults
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScanUpload, type ScannedImage } from "@/components/scan/ScanUpload";
import { GradingResults, type GradingResultsData } from "@/components/results/GradingResults";
import { type TeacherOverride } from "@/components/results/QuestionResult";
import { detectQRCode, validateBatchWorksheet, type QRDetectionResult } from "@/utils/qr-detection";
import { scanService, type ScanResult } from "@/services/scan.service";
import { worksheetService, type Worksheet } from "@/services/worksheet.service";
import { classService } from "@/services/class.service";
import { useLocalDbStore } from "@/store/localDbStore";
import { setServerClassId } from "@/lib/local-db";
import { toast } from "sonner";
import {
  Camera,
  Upload,
  QrCode,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  FileText,
  Users,
  Sparkles,
  RotateCcw,
  Eye,
} from "lucide-react";

// ============================================================================
// Types
// ============================================================================

type FlowStep = "capture" | "detect" | "upload" | "extract" | "results";

interface StepState {
  capture: { completed: boolean; images: ScannedImage[] };
  detect: { completed: boolean; worksheetId: string | null; qrResults: QRDetectionResult[] };
  upload: { completed: boolean; scanId: string | null; progress: number };
  extract: { completed: boolean; scanResult: ScanResult | null; progress: number };
  results: { completed: boolean };
}

// ============================================================================
// Helper Functions
// ============================================================================

function isRequestCancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") {
    return true;
  }
  if (error && typeof error === "object") {
    const err = error as { code?: string; name?: string };
    if (err.code === "ERR_CANCELED" || err.name === "CanceledError") {
      return true;
    }
  }
  return false;
}

function getStepNumber(step: FlowStep): number {
  const steps: FlowStep[] = ["capture", "detect", "upload", "extract", "results"];
  return steps.indexOf(step) + 1;
}

function getStepProgress(step: FlowStep, stepState: StepState): number {
  const stepNumber = getStepNumber(step);
  const totalSteps = 5;

  // Base progress from completed steps
  let progress = ((stepNumber - 1) / totalSteps) * 100;

  // Add progress within current step
  if (step === "upload" && stepState.upload.progress > 0) {
    progress += (stepState.upload.progress / totalSteps);
  } else if (step === "extract" && stepState.extract.progress > 0) {
    progress += (stepState.extract.progress / totalSteps);
  }

  return Math.min(progress, 100);
}

// Transform ScanResult to GradingResultsData format
function transformToGradingData(scanResult: ScanResult, worksheetTitle: string): GradingResultsData {
  return {
    sessionId: scanResult.scanId,
    worksheetId: scanResult.worksheetId,
    worksheetTitle: worksheetTitle || scanResult.worksheetTitle,
    studentName: null, // Could be extracted from sheet number mapping
    studentSheetNumber: scanResult.sheetNumber || null,
    gradedAt: scanResult.updatedAt || scanResult.createdAt,
    answers: (scanResult.gradingResults || []).map((gr) => ({
      questionId: gr.questionId,
      questionType: gr.questionType,
      questionPrompt: `Question ${gr.questionId}`, // Would come from worksheet
      studentAnswer: Array.isArray(gr.extractedAnswer) ? gr.extractedAnswer.join(", ") : gr.extractedAnswer,
      correctAnswer: Array.isArray(gr.expectedAnswer) ? gr.expectedAnswer.join(", ") : gr.expectedAnswer,
      autoGradable: true,
      autoGradingResult: gr.isCorrect ? "correct" : "incorrect",
      points: gr.score,
      maxPoints: gr.maxScore,
    })),
    totalPoints: scanResult.totalScore || 0,
    maxPoints: scanResult.maxScore || 0,
    autoGradedCount: (scanResult.gradingResults || []).length,
    needsReviewCount: scanResult.requiresReview ? 1 : 0,
  };
}

// ============================================================================
// Sub-Components
// ============================================================================

interface StepIndicatorProps {
  currentStep: FlowStep;
  stepState: StepState;
}

function StepIndicator({ currentStep, stepState }: StepIndicatorProps) {
  const steps: { key: FlowStep; label: string; icon: React.ElementType }[] = [
    { key: "capture", label: "Capture", icon: Camera },
    { key: "detect", label: "Detect QR", icon: QrCode },
    { key: "upload", label: "Upload", icon: Upload },
    { key: "extract", label: "Grade", icon: Sparkles },
    { key: "results", label: "Results", icon: CheckCircle2 },
  ];

  const currentIdx = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="w-full">
      {/* Progress bar */}
      <Progress value={getStepProgress(currentStep, stepState)} className="h-2 mb-4" />

      {/* Step labels - responsive */}
      <div className="hidden sm:flex justify-between">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isComplete = idx < currentIdx;
          const isCurrent = idx === currentIdx;

          return (
            <div
              key={step.key}
              className={`flex flex-col items-center gap-1 ${
                isComplete ? "text-primary" : isCurrent ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  isComplete
                    ? "bg-primary border-primary text-primary-foreground"
                    : isCurrent
                    ? "border-primary text-primary"
                    : "border-muted-foreground/30"
                }`}
              >
                {isComplete ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
              </div>
              <span className="text-xs font-medium">{step.label}</span>
            </div>
          );
        })}
      </div>

      {/* Mobile: current step only */}
      <div className="sm:hidden flex items-center justify-center gap-2">
        {steps.map((step, idx) => {
          const isCurrent = idx === currentIdx;
          if (!isCurrent) return null;
          const Icon = step.icon;
          return (
            <div key={step.key} className="flex items-center gap-2">
              <Icon className="h-5 w-5" />
              <span className="font-medium">
                Step {idx + 1}: {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface WorksheetSelectProps {
  worksheets: Worksheet[];
  selectedId: string;
  onSelect: (id: string) => void;
  loading: boolean;
  detectedId: string | null;
}

function WorksheetSelect({ worksheets, selectedId, onSelect, loading, detectedId }: WorksheetSelectProps) {
  if (loading) {
    return <div className="h-10 bg-muted animate-pulse rounded-md" />;
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Worksheet Template</label>
      <Select value={selectedId} onValueChange={onSelect}>
        <SelectTrigger>
          <SelectValue placeholder="Select a worksheet..." />
        </SelectTrigger>
        <SelectContent>
          {worksheets.map((ws) => (
            <SelectItem key={ws.id} value={ws.id}>
              <span className="flex items-center gap-2">
                {ws.title} (Grade {ws.grade})
                {ws.id === detectedId && (
                  <Badge variant="secondary" className="text-xs">
                    Detected
                  </Badge>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {detectedId && selectedId === detectedId && (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Worksheet detected from QR code
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function ScanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const worksheetIdFromUrl = searchParams.get("worksheet_id");

  // Flow state
  const [currentStep, setCurrentStep] = useState<FlowStep>("capture");
  const [stepState, setStepState] = useState<StepState>({
    capture: { completed: false, images: [] },
    detect: { completed: false, worksheetId: null, qrResults: [] },
    upload: { completed: false, scanId: null, progress: 0 },
    extract: { completed: false, scanResult: null, progress: 0 },
    results: { completed: false },
  });

  // Form state
  const [selectedWorksheetId, setSelectedWorksheetId] = useState<string>(worksheetIdFromUrl || "");
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [manualMode, setManualMode] = useState(false);

  // Data state
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [worksheetsLoading, setWorksheetsLoading] = useState(true);
  const [overrides, setOverrides] = useState<Record<string, TeacherOverride>>({});
  const [savingCorrections, setSavingCorrections] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Local DB state for classes
  const db = useLocalDbStore((s) => s.db);
  const localClasses = useLocalDbStore((s) => s.classes);
  const isLocalDbConnected = useLocalDbStore((s) => s.isConnected);
  const markDirty = useLocalDbStore((s) => s.markDirty);

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Fetch worksheets on mount
  useEffect(() => {
    const controller = new AbortController();

    const fetchWorksheets = async () => {
      try {
        setWorksheetsLoading(true);
        const response = await worksheetService.getWorksheets(undefined, controller.signal);
        if (!controller.signal.aborted) {
          setWorksheets(response.items);
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to load worksheets:", err);
          toast.error("Failed to load worksheets");
        }
      } finally {
        if (!controller.signal.aborted) {
          setWorksheetsLoading(false);
        }
      }
    };

    fetchWorksheets();

    return () => controller.abort();
  }, []);

  // Auto-select first class when DB connects
  useEffect(() => {
    if (isLocalDbConnected && localClasses.length > 0 && !selectedClassId) {
      setSelectedClassId(localClasses[0].id);
    }
  }, [isLocalDbConnected, localClasses, selectedClassId]);

  // Get selected worksheet details
  const selectedWorksheet = useMemo(
    () => worksheets.find((w) => w.id === selectedWorksheetId),
    [worksheets, selectedWorksheetId]
  );

  // Handle images ready from ScanUpload
  const handleImagesReady = useCallback((images: ScannedImage[]) => {
    setStepState((prev) => ({
      ...prev,
      capture: { completed: images.length > 0, images },
    }));
  }, []);

  // Process captured images - detect QR codes
  const handleProcessImages = useCallback(async () => {
    const images = stepState.capture.images;
    if (images.length === 0) {
      toast.error("Please capture at least one image");
      return;
    }

    setCurrentStep("detect");
    setError(null);

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Detect QR codes in all images
      const qrResults: QRDetectionResult[] = [];

      for (const image of images) {
        if (controller.signal.aborted) break;

        const result = await detectQRCode(image.file);
        qrResults.push(result);
      }

      if (controller.signal.aborted) return;

      // Validate batch (all should be same worksheet)
      const validation = validateBatchWorksheet(qrResults);

      if (validation.valid && validation.worksheetId) {
        // Auto-detected worksheet
        setSelectedWorksheetId(validation.worksheetId);
        setStepState((prev) => ({
          ...prev,
          detect: {
            completed: true,
            worksheetId: validation.worksheetId,
            qrResults,
          },
        }));
        toast.success(`Worksheet detected: ${validation.worksheetId}`);

        // Auto-proceed to upload
        handleUpload(validation.worksheetId, controller.signal);
      } else if (qrResults.some((r) => r.success)) {
        // Partial detection - use first detected worksheet
        const firstValid = qrResults.find((r) => r.success);
        if (firstValid?.worksheetId) {
          setSelectedWorksheetId(firstValid.worksheetId);
          setStepState((prev) => ({
            ...prev,
            detect: {
              completed: true,
              worksheetId: firstValid.worksheetId,
              qrResults,
            },
          }));
          toast.info("QR detected - please verify worksheet");
        }
      } else {
        // No QR detected - manual selection needed
        setManualMode(true);
        setStepState((prev) => ({
          ...prev,
          detect: {
            completed: false,
            worksheetId: null,
            qrResults,
          },
        }));
        toast.warning("No QR code detected. Please select worksheet manually.");
      }
    } catch (err) {
      if (!isRequestCancelled(err)) {
        console.error("QR detection failed:", err);
        setError(err instanceof Error ? err.message : "QR detection failed");
        setManualMode(true);
      }
    }
  }, [stepState.capture.images]);

  // Sync class to server (if needed)
  const syncClassToServer = async (
    localClassId: string,
    signal: AbortSignal
  ): Promise<string | undefined> => {
    const localClass = localClasses.find((c) => c.id === localClassId);
    if (!localClass) return undefined;

    if (localClass.serverClassId) {
      return localClass.serverClassId;
    }

    try {
      const serverClass = await classService.createClass(
        { name: localClass.name, grade: localClass.grade || undefined },
        signal
      );

      if (db) {
        setServerClassId(db, localClassId, serverClass.classId);
        markDirty();
      }

      return serverClass.classId;
    } catch (err) {
      if (!isRequestCancelled(err)) {
        console.error("Failed to sync class:", err);
      }
      return undefined;
    }
  };

  // Upload images to backend
  const handleUpload = useCallback(
    async (worksheetId?: string, signal?: AbortSignal) => {
      const wsId = worksheetId || selectedWorksheetId;
      if (!wsId) {
        toast.error("Please select a worksheet");
        return;
      }

      const images = stepState.capture.images;
      if (images.length === 0) {
        toast.error("No images to upload");
        return;
      }

      setCurrentStep("upload");
      setError(null);

      const controller = signal ? { signal } : new AbortController();
      if (!signal) {
        abortControllerRef.current?.abort();
        abortControllerRef.current = controller as AbortController;
      }
      const abortSignal = signal || (controller as AbortController).signal;

      try {
        // Sync class if selected
        let serverClassId: string | undefined;
        if (selectedClassId) {
          serverClassId = await syncClassToServer(selectedClassId, abortSignal);
        }

        // Upload first image (single scan for now)
        const firstImage = images[0];

        setStepState((prev) => ({
          ...prev,
          upload: { ...prev.upload, progress: 30 },
        }));

        const uploadResult = await scanService.uploadScan(
          firstImage.file,
          wsId,
          undefined, // sheetNumber
          serverClassId,
          abortSignal
        );

        if (abortSignal.aborted) return;

        setStepState((prev) => ({
          ...prev,
          upload: { completed: true, scanId: uploadResult.scanId, progress: 100 },
        }));

        toast.success("Upload complete!");

        // Proceed to extraction
        handleExtract(uploadResult.scanId, abortSignal);
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Upload failed:", err);
          const message = err instanceof Error ? err.message : "Upload failed";
          setError(message);
          toast.error(message);
        }
      }
    },
    [selectedWorksheetId, selectedClassId, stepState.capture.images, localClasses, db, markDirty]
  );

  // Trigger extraction and grading
  const handleExtract = useCallback(async (scanId: string, signal?: AbortSignal) => {
    setCurrentStep("extract");
    setError(null);

    const controller = signal ? { signal } : new AbortController();
    if (!signal) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = controller as AbortController;
    }
    const abortSignal = signal || (controller as AbortController).signal;

    try {
      setStepState((prev) => ({
        ...prev,
        extract: { ...prev.extract, progress: 30 },
      }));

      const result = await scanService.extractScan(scanId, abortSignal);

      if (abortSignal.aborted) return;

      setStepState((prev) => ({
        ...prev,
        extract: { completed: true, scanResult: result, progress: 100 },
      }));

      toast.success("Grading complete!");

      // Move to results
      setCurrentStep("results");
      setStepState((prev) => ({
        ...prev,
        results: { completed: true },
      }));
    } catch (err) {
      if (!isRequestCancelled(err)) {
        console.error("Extraction failed:", err);
        const message = err instanceof Error ? err.message : "Grading failed";
        setError(message);
        toast.error(message);
      }
    }
  }, []);

  // Handle manual worksheet selection and proceed
  const handleManualProceed = useCallback(() => {
    if (!selectedWorksheetId) {
      toast.error("Please select a worksheet");
      return;
    }

    setStepState((prev) => ({
      ...prev,
      detect: {
        ...prev.detect,
        completed: true,
        worksheetId: selectedWorksheetId,
      },
    }));

    handleUpload(selectedWorksheetId);
  }, [selectedWorksheetId, handleUpload]);

  // Save teacher corrections
  const handleSaveCorrections = useCallback(
    async (newOverrides: Record<string, TeacherOverride>) => {
      const scanId = stepState.upload.scanId;
      if (!scanId) return;

      setSavingCorrections(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        // Transform overrides to corrections format
        const corrections = Object.values(newOverrides)
          .filter((o) => o.overrideStatus !== null)
          .map((o) => ({
            questionId: o.questionId,
            correct: o.overrideStatus === "correct" ? true : o.overrideStatus === "incorrect" ? false : undefined,
            feedback: o.teacherNote || undefined,
          }));

        if (corrections.length > 0) {
          const result = await scanService.applyCorrections(scanId, corrections, controller.signal);

          setStepState((prev) => ({
            ...prev,
            extract: { ...prev.extract, scanResult: result },
          }));

          setOverrides({});
          toast.success("Corrections saved!");
        }
      } catch (err) {
        if (!isRequestCancelled(err)) {
          console.error("Failed to save corrections:", err);
          toast.error("Failed to save corrections");
        }
      } finally {
        setSavingCorrections(false);
      }
    },
    [stepState.upload.scanId]
  );

  // Reset flow to start over
  const handleReset = useCallback(() => {
    abortControllerRef.current?.abort();
    setCurrentStep("capture");
    setStepState({
      capture: { completed: false, images: [] },
      detect: { completed: false, worksheetId: null, qrResults: [] },
      upload: { completed: false, scanId: null, progress: 0 },
      extract: { completed: false, scanResult: null, progress: 0 },
      results: { completed: false },
    });
    setSelectedWorksheetId(worksheetIdFromUrl || "");
    setManualMode(false);
    setError(null);
    setOverrides({});
  }, [worksheetIdFromUrl]);

  // Go back one step
  const handleBack = useCallback(() => {
    const steps: FlowStep[] = ["capture", "detect", "upload", "extract", "results"];
    const currentIdx = steps.indexOf(currentStep);
    if (currentIdx > 0) {
      // For capture step, just go back
      // For other steps, might need to reset state
      if (currentStep === "detect") {
        setCurrentStep("capture");
      } else if (currentStep === "upload" || currentStep === "extract") {
        // Go back to detect/manual selection
        setCurrentStep("detect");
        setManualMode(true);
      } else if (currentStep === "results") {
        // Can't really go back from results, offer reset
        handleReset();
      }
    }
  }, [currentStep, handleReset]);

  // Prepare grading data for display
  const gradingData = useMemo<GradingResultsData | null>(() => {
    const scanResult = stepState.extract.scanResult;
    if (!scanResult) return null;
    return transformToGradingData(scanResult, selectedWorksheet?.title || "");
  }, [stepState.extract.scanResult, selectedWorksheet]);

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Scan Worksheet</h1>
        <p className="text-muted-foreground">
          Capture, detect, and automatically grade student worksheets
        </p>
      </div>

      {/* Progress indicator */}
      <StepIndicator currentStep={currentStep} stepState={stepState} />

      {/* Error display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step 1: Capture */}
      {currentStep === "capture" && (
        <div className="space-y-4">
          <ScanUpload
            onImagesReady={handleImagesReady}
            maxPages={5}
            isUploading={false}
          />

          {stepState.capture.images.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                className="flex-1"
                onClick={handleProcessImages}
              >
                <QrCode className="mr-2 h-5 w-5" />
                Detect Worksheet
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              {/* Optional: skip QR and go manual */}
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  setCurrentStep("detect");
                  setManualMode(true);
                }}
              >
                <FileText className="mr-2 h-5 w-5" />
                Select Manually
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Detect / Manual Selection */}
      {currentStep === "detect" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {manualMode ? (
                <>
                  <FileText className="h-5 w-5" />
                  Select Worksheet
                </>
              ) : (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Detecting QR Code...
                </>
              )}
            </CardTitle>
            <CardDescription>
              {manualMode
                ? "Choose the worksheet template that matches your scan"
                : "Scanning images for worksheet QR codes"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* QR Detection Results */}
            {stepState.detect.qrResults.length > 0 && (
              <div className="space-y-2">
                {stepState.detect.qrResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded-md ${
                      result.success ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {result.success ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                    <span className="text-sm">
                      Page {idx + 1}:{" "}
                      {result.success
                        ? `Detected worksheet ${result.worksheetId}`
                        : result.error || "No QR found"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Worksheet selection */}
            <WorksheetSelect
              worksheets={worksheets}
              selectedId={selectedWorksheetId}
              onSelect={setSelectedWorksheetId}
              loading={worksheetsLoading}
              detectedId={stepState.detect.worksheetId}
            />

            {/* Class selection */}
            {isLocalDbConnected && localClasses.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Class (Optional)
                </label>
                <Select value={selectedClassId} onValueChange={setSelectedClassId}>
                  <SelectTrigger>
                    <SelectValue placeholder="No class selected" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No class</SelectItem>
                    {localClasses.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name} {cls.grade ? `(Grade ${cls.grade})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleManualProceed}
                disabled={!selectedWorksheetId}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload and Grade
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Upload */}
      {currentStep === "upload" && (
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            </div>
            <CardTitle>Uploading Worksheet</CardTitle>
            <CardDescription>
              Sending your scan to the server...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={stepState.upload.progress} className="h-2" />
          </CardContent>
        </Card>
      )}

      {/* Step 4: Extract */}
      {currentStep === "extract" && (
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Sparkles className="h-16 w-16 text-primary animate-pulse" />
            </div>
            <CardTitle>Grading Worksheet</CardTitle>
            <CardDescription>
              AI is extracting answers and grading your worksheet...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress value={stepState.extract.progress} className="h-2" />
            <p className="text-center text-sm text-muted-foreground mt-4">
              This may take 10-30 seconds
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Results */}
      {currentStep === "results" && gradingData && (
        <div className="space-y-4">
          <GradingResults
            data={gradingData}
            overrides={overrides}
            onOverridesChange={setOverrides}
            onSave={handleSaveCorrections}
            isSaving={savingCorrections}
          />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Scan Another
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/scans/${stepState.upload.scanId}`)}
            >
              <Eye className="mr-2 h-4 w-4" />
              View Full Details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
